const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1706';

// Helper function to handle API responses
const handleApiResponse = async (response) => {
  // Get the response text first
  const responseText = await response.text();

  // Check if response is HTML (error page)
  if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
    console.error('Received HTML instead of JSON:', responseText.substring(0, 200));
    throw new Error('Server returned HTML instead of JSON. Service may be down or misconfigured.');
  }

  // Check if response is empty
  if (!responseText || responseText.trim() === '') {
    console.error('Received empty response');
    throw new Error('Server returned empty response');
  }

  // Try to parse JSON
  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    console.error('JSON Parse Error:', parseError);
    throw new Error(`Failed to parse JSON response: ${parseError.message}`);
  }

  // Check if response indicates an error
  if (!response.ok) {
    const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
    console.error('API Error Response:', data);
    throw new Error(errorMessage);
  }

  return data;
};

// Fetch all collections
export const fetchCollections = async () => {
  try {
    const url = `${API_URL}/collections`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    const data = await handleApiResponse(response);

    // Handle different possible response structures
    let collections = [];
    if (Array.isArray(data)) {
      collections = data;
    } else if (data.data && Array.isArray(data.data)) {
      collections = data.data;
    } else if (data.collections && Array.isArray(data.collections)) {
      collections = data.collections;
    } else {
      console.warn('Unexpected response structure:', data);
      collections = [];
    }

    return {
      success: true,
      data: collections,
      total: data.total || collections.length
    };
  } catch (error) {
    console.error('Error fetching collections:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Fetch single collection by ID
export const fetchCollectionById = async (id) => {
  try {
    const url = `${API_URL}/collections/${id}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });

    const data = await handleApiResponse(response);

    // Handle different possible response structures
    let collection = null;
    if (data.data) {
      collection = data.data;
    } else if (data.collection) {
      collection = data.collection;
    } else if (data._id) {
      collection = data;
    }

    if (!collection) {
      throw new Error('Collection not found in response');
    }

    return {
      success: true,
      data: collection
    };
  } catch (error) {
    console.error('Error fetching collection by ID:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// Stock management helpers for collections

// Check if a color has stock in a collection
export const hasStockForColor = (collection, colorName, size = null, features = []) => {
  if (!collection || !collection.stockCombinations) return false;

  // For non-color-indexed products, ignore colorName parameter
  if (!collection.isColorIndexed) {
    return collection.stockCombinations.some(comb => {
      // Check size match if specified
      if (size && comb.size !== size) return false;

      // Check features match
      if (features.length > 0) {
        const hasMatchingFeatures = features.every(reqFeature =>
          comb.features && comb.features.some(combFeature =>
            combFeature.name === reqFeature.name &&
            combFeature.value === reqFeature.value
          )
        );
        if (!hasMatchingFeatures) return false;
      }

      return comb.quantity > 0;
    });
  }

  // For color-indexed products, filter by color
  const colorStockCombinations = collection.stockCombinations.filter(comb => comb.color === colorName);

  if (colorStockCombinations.length === 0) {
    return false;
  }

  // If no specific size or features requested, check if any stock exists
  if (!size && features.length === 0) {
    return colorStockCombinations.some(comb => comb.quantity > 0);
  }

  // Check for specific size/feature combination
  return colorStockCombinations.some(comb => {
    if (size && comb.size !== size) return false;
    if (features.length > 0) {
      const hasMatchingFeatures = features.every(reqFeature =>
        comb.features && comb.features.some(combFeature =>
          combFeature.name === reqFeature.name &&
          combFeature.value === reqFeature.value
        )
      );
      if (!hasMatchingFeatures) return false;
    }
    return comb.quantity > 0;
  });
};

// Get available stock quantity for a color/size/features combination
export const getStockQuantity = (collection, colorName = null, size = null, features = []) => {
  if (!collection || !collection.stockCombinations) return 0;

  // For non-color-indexed products, ignore colorName parameter and sum all combinations
  if (!collection.isColorIndexed) {
    return collection.stockCombinations.reduce((total, comb) => {
      // Check size match if specified
      if (size && comb.size !== size) return total;

      // Check features match
      if (features.length > 0) {
        const hasMatchingFeatures = features.every(reqFeature =>
          comb.features && comb.features.some(combFeature =>
            combFeature.name === reqFeature.name &&
            combFeature.value === reqFeature.value
          )
        );
        if (!hasMatchingFeatures) return total;
      }

      return total + (comb.quantity || 0);
    }, 0);
  }

  // For color-indexed products
  return collection.stockCombinations.reduce((total, comb) => {
    // Check color match if specified
    if (colorName && comb.color !== colorName) return total;

    // Check size match if specified
    if (size && comb.size !== size) return total;

    // Check features match
    if (features.length > 0) {
      const hasMatchingFeatures = features.every(reqFeature =>
        comb.features && comb.features.some(combFeature =>
          combFeature.name === reqFeature.name &&
          combFeature.value === reqFeature.value
        )
      );
      if (!hasMatchingFeatures) return total;
    }

    return total + (comb.quantity || 0);
  }, 0);
};

// Get out-of-stock colors for a collection
export const getOutOfStockColors = (collection, size = null, features = []) => {
  if (!collection || !collection.stockCombinations) return [];

  // For non-color-indexed products, no colors are out of stock
  if (!collection.isColorIndexed) {
    return [];
  }

  // Get unique color names from stock combinations
  const colorNamesInCollection = new Set();
  collection.stockCombinations.forEach(comb => {
    if (comb.color) {
      colorNamesInCollection.add(comb.color);
    }
  });

  // Filter out colors that don't have stock
  const outOfStockColors = Array.from(colorNamesInCollection).filter(colorName => 
    !hasStockForColor(collection, colorName, size, features)
  );

  return outOfStockColors;
};
